import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";

export const SKU_IMPORT_TEMPLATE_URL =
  "https://content.swageazy.com/templates/InventoryItems_Sku_Update_Template.csv";

interface SkuImportCsvPopupProps {
  open: boolean;
  inventoryId: number;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { note: string; csvFile: File }) => Promise<void>;
}

export default function SkuImportCsvPopup({
  open,
  inventoryId,
  isSubmitting = false,
  onClose,
  onSubmit,
}: SkuImportCsvPopupProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [note, setNote] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const isSubmitLockedRef = useRef(false);

  const submitInProgress = isSubmitting || isSubmitLocked;

  useEffect(() => {
    if (!open) return;
    setNote("");
    setCsvFile(null);
    setFileError("");
    setIsSubmitLocked(false);
    isSubmitLockedRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (submitInProgress) return;
      const file = event.target.files?.[0] || null;
      event.target.value = "";
      if (!file) return;

      if (!file.name.toLowerCase().endsWith(".csv")) {
        setCsvFile(null);
        setFileError("Please upload a CSV file");
        return;
      }

      setCsvFile(file);
      setFileError("");
    } catch (error) {
      console.error("Failed to select CSV file:", error);
      setFileError("Failed to select CSV file");
    }
  };

  const handleRemoveFile = () => {
    if (submitInProgress) return;
    setCsvFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const link = document.createElement("a");
      link.href = SKU_IMPORT_TEMPLATE_URL;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = "InventoryItems_Sku_Update_Template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download SKU update template:", error);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitLockedRef.current || submitInProgress) {
      return;
    }
    if (!note.trim() || !csvFile) {
      return;
    }

    isSubmitLockedRef.current = true;
    setIsSubmitLocked(true);

    try {
      await onSubmit({ note: note.trim(), csvFile });
    } catch (error) {
      console.error("Failed to import SKU update CSV:", error);
    } finally {
      isSubmitLockedRef.current = false;
      setIsSubmitLocked(false);
    }
  };

  const handleClose = () => {
    if (submitInProgress) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            position: "relative",
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Backdrop
        open={submitInProgress}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: theme.palette.customColors.white,
          borderRadius: "16px",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Update SKUs via CSV
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Emergency use only. Renames existing client SKUs. Catalog SKU,
              ledger, and shipment lines are not rewritten.
            </CustomTypography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitInProgress}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  INVENTORY ID
                </CustomTypography>
                <CustomTypography variant="body2" sx={{ mt: 0.5 }}>
                  {inventoryId}
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  CSV columns: inventory_id, current_sku, new_sku. Every
                  inventory_id must equal {inventoryId}. Max 1000 rows.
                  Duplicate current_sku or new_sku in the file fails the whole
                  request. Rows where current_sku equals new_sku are skipped.
                  Missing current_sku or an already-active new_sku fails the
                  whole import.
                </CustomTypography>
                <Box sx={{ mt: 1.5 }}>
                  <AppButton
                    label="Download Template"
                    variant="grayscale"
                    onClick={handleDownloadTemplate}
                    disabled={submitInProgress}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  CSV FILE
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Upload the filled SKU update template
                </CustomTypography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={handleFileChange}
                />
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <AppButton
                    label={csvFile ? "Replace CSV" : "Choose CSV"}
                    variant="grayscale"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitInProgress}
                  />
                  {csvFile && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      minWidth={0}
                    >
                      <CustomTypography variant="body2" fontWeight={600}>
                        {csvFile.name}
                      </CustomTypography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        disabled={submitInProgress}
                        aria-label="Remove CSV file"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {fileError && (
                  <CustomTypography
                    variant="body3"
                    sx={{
                      mt: 0.75,
                      display: "block",
                      color: theme.palette.error.main,
                    }}
                  >
                    {fileError}
                  </CustomTypography>
                )}
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  NOTE
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  This note is recorded against the SKU rename import
                </CustomTypography>
                <TextField
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  placeholder="Enter SKU update note"
                  multiline
                  minRows={3}
                  disabled={submitInProgress}
                  sx={{
                    mt: 1,
                    "& .MuiInputBase-root": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton
                label="Cancel"
                onClick={handleClose}
                variant="grayscale"
                disabled={submitInProgress}
              />
              <AppButton
                label="Import CSV"
                onClick={() => {
                  if (submitInProgress) return;
                  void handleSubmit();
                }}
                variant="primary"
                disabled={submitInProgress || !note.trim() || !csvFile}
                customStyles={{
                  pointerEvents: submitInProgress ? "none" : "auto",
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
